import { useMemo, useState } from 'react';

import { di } from 'react-magnetic-di';

import type { BaseFieldConfiguration, ViewConfiguration } from '../../../common/types';
import type { ViewType } from '../../../common/types/context';
import type { ExecutionContext } from '../../../types';

const VIEW_CONFIGURATIONS = new Map<ViewType, ViewConfiguration>();

/**
 * Loads view configuration for a given view type.
 *
 * @internal Use useLoadViewConfiguration hook.
 */
export const loadViewConfiguration = (viewType: ViewType, configuration: ViewConfiguration) => {
	if (!VIEW_CONFIGURATIONS.has(viewType)) {
		VIEW_CONFIGURATIONS.set(viewType, configuration);
	}
};

/**
 * Returns view configuration for a given view type.
 */
export const getViewConfiguration = <T extends BaseFieldConfiguration = BaseFieldConfiguration>(
	viewType: ViewType,
): ViewConfiguration<T, ExecutionContext> | undefined =>
	VIEW_CONFIGURATIONS.get(viewType) as ViewConfiguration<T, ExecutionContext>;

/**
 * Clears view configurations.
 *
 * @internal For tests only.
 */
export const clearViewConfigurations = () => {
	VIEW_CONFIGURATIONS.clear();
};

/**
 * Loads UI modifications view configuration once (legacy implementation).
 * Uses useState to load view configuration.
 * @deprecated Use useLoadViewConfiguration instead for improved performance.
 */
export const useLoadViewConfigurationLegacy = (
	viewType: ViewType,
	configurationClass: new () => ViewConfiguration,
) => {
	di(loadViewConfiguration);
	/**
	 * Uses useState to load view configuration.
	 */
	useState(() => {
		loadViewConfiguration(viewType, new configurationClass());
	});
};

/**
 * Loads UI modifications view configuration once.
 * Uses useMemo to load view configuration during the first render, before mount.
 */
export const useLoadViewConfiguration = (
	viewType: ViewType,
	configurationClass: new () => ViewConfiguration,
) => {
	di(loadViewConfiguration);
	/**
	 * Uses useMemo to load view configuration during the first render, before mount.
	 */
	useMemo(() => {
		loadViewConfiguration(viewType, new configurationClass());
	}, [viewType, configurationClass]);
};
